<script lang="ts">
  import { onMount, untrack } from 'svelte';
  import {
    CandlestickSeries,
    HistogramSeries,
    type IChartApi,
    type ISeriesApi,
    type ISeriesMarkersPluginApi,
    type Time,
    type UTCTimestamp,
    createChart,
    createSeriesMarkers,
  } from 'lightweight-charts';
  import type { CandleDoc, SessionTradeDoc } from '$lib/api/charts';
  import { themeStore } from '$lib/stores/theme.svelte';
  import {
    type ChartTheme,
    type OhlcCandle,
    etWallClockToUtcSeconds,
    getChartTheme,
    isoToUtcSeconds,
    tickMarkFormatterET,
    toHeikinAshi,
    withAlpha,
  } from '$lib/utils/chart-helpers';

  // ============================================================
  // <CandlestickChart>  -  Fase 5.
  //
  // Mejoras vs v1 (Plotly):
  //   - Eje X temporal real (Lightweight UTC seconds).
  //   - Volume pane debajo del price pane (color-coded).
  //   - Trade markers en single plugin (no N traces).
  //   - Heikin Ashi toggle nativo.
  //   - Crosshair tooltip enriquecido con OHLCV.
  //   - Session boundary marker en SESSION_START.
  //   - Theme dinamico (lee CSS vars).
  // ============================================================

  interface Props {
    candles: CandleDoc[];
    trades?: SessionTradeDoc[];
    sessionStartIso?: string | null;
    sessionEndIso?: string | null;
    heikinAshi?: boolean;
    height?: number;
    /**
     * Callback con la IChartApi cuando el chart está listo. Permite al
     * parent sincronizar zoom con otros charts (ej. RenkoChart hermano).
     */
    onReady?: (chart: IChartApi) => void;
  }

  let {
    candles,
    trades = [],
    sessionStartIso = null,
    sessionEndIso = null,
    heikinAshi = false,
    height = 520,
    onReady,
  }: Props = $props();

  let container: HTMLDivElement;
  let chart: IChartApi | null = null;
  let candleSeries: ISeriesApi<'Candlestick'> | null = null;
  let volumeSeries: ISeriesApi<'Histogram'> | null = null;
  let markersPlugin: ISeriesMarkersPluginApi<Time> | null = null;

  let theme = $state<ChartTheme>({
    background: '#1a1a1a',
    foreground: '#f5f1e8',
    border: '#404040',
    grid: '#404040',
    success: '#4ade80',
    destructive: '#f87171',
    primary: '#D87807',
    gold: '#d4af37',
    muted: '#b0b0b0',
  });

  // ----- Transform raw candles → OhlcCandle[] con UTC seconds + HA opcional -----
  function getOhlc(): OhlcCandle[] {
    const base: OhlcCandle[] = candles.map((c) => ({
      time: etWallClockToUtcSeconds(c.time),
      open: c.open,
      high: c.high,
      low: c.low,
      close: c.close,
    }));
    base.sort((a, b) => a.time - b.time);
    // Forzar monotonia estricta: si hay dups o el helper devolvio un time
    // <= al anterior por edge de DST o data sucia, shift +1s para preservar
    // el orden. Lightweight rechaza datos no-monotonos con assertion fatal.
    const monotonic: OhlcCandle[] = [];
    let lastT = 0;
    for (const c of base) {
      let t = c.time;
      if (t <= lastT) t = lastT + 1;
      lastT = t;
      monotonic.push({ ...c, time: t });
    }
    return heikinAshi ? toHeikinAshi(monotonic) : monotonic;
  }

  function getVolumeData() {
    // Mismo patron de monotonia estricta que getOhlc.
    const raw = candles.map((c) => ({
      time: etWallClockToUtcSeconds(c.time),
      value: c.volume,
      color:
        c.close >= c.open
          ? withAlpha(theme.success, 0.4)
          : withAlpha(theme.destructive, 0.4),
    }));
    raw.sort((a, b) => a.time - b.time);
    const out: Array<{ time: UTCTimestamp; value: number; color: string }> = [];
    let lastT = 0;
    for (const item of raw) {
      let t = item.time;
      if (t <= lastT) t = lastT + 1;
      lastT = t;
      out.push({ time: t as UTCTimestamp, value: item.value, color: item.color });
    }
    return out;
  }

  // ----- Rango temporal de las candles disponibles -----
  // Lightweight extiende el time scale para mostrar TODOS los datos que le
  // pases. Si un trade marker cae despues del ultimo candle, el chart se
  // estira y aparecen "lineas fantasma" en zonas sin candle real.
  //
  // Solucion: filtrar markers y connection lines al rango [firstCandleT, lastCandleT].
  // Los trades que esten fuera (e.g. el live trader sigue activo y emitio
  // un trade despues del ultimo candle cacheado) se descartan visualmente.
  function getCandleTimeRange(): { firstT: number; lastT: number } | null {
    if (candles.length === 0) return null;
    let firstT = Number.POSITIVE_INFINITY;
    let lastT = Number.NEGATIVE_INFINITY;
    for (const c of candles) {
      const t = etWallClockToUtcSeconds(c.time);
      if (t === 0) continue;
      if (t < firstT) firstT = t;
      if (t > lastT) lastT = t;
    }
    if (!Number.isFinite(firstT) || !Number.isFinite(lastT)) return null;
    return { firstT, lastT };
  }

  // ----- Build markers para trades -----
  // Numeracion #N global por orden de entryTime ASC: el mismo numero aparece
  // en la entrada (LONG #N / SHORT #N) y en la salida (+$XXX #N / -$XXX #N),
  // asi el operador puede matchear visualmente entry↔exit del mismo trade.
  function buildMarkers() {
    type Marker = {
      time: UTCTimestamp;
      position: 'aboveBar' | 'belowBar';
      color: string;
      shape: 'arrowUp' | 'arrowDown' | 'circle' | 'square';
      text?: string;
    };
    const range = getCandleTimeRange();
    if (!range) return [];

    // Asigno trade# por orden de entryTime, antes de filtrar por range,
    // para que el numero sea estable aun si la entrada cae fuera del range.
    const numbered = trades
      .map((t) => ({ trade: t, entryT: isoToUtcSeconds(t.entryTime) }))
      .filter((x): x is { trade: typeof x.trade; entryT: number } => x.entryT != null)
      .sort((a, b) => a.entryT - b.entryT)
      .map((x, i) => ({ trade: x.trade, entryT: x.entryT, num: i + 1 }));

    const markers: Marker[] = [];
    for (const { trade: t, entryT, num } of numbered) {
      if (t.side && entryT >= range.firstT && entryT <= range.lastT) {
        markers.push({
          time: entryT as UTCTimestamp,
          position: t.side === 'long' ? 'belowBar' : 'aboveBar',
          color: t.side === 'long' ? theme.success : theme.destructive,
          shape: t.side === 'long' ? 'arrowUp' : 'arrowDown',
          text: `${t.side.toUpperCase()} #${num}`,
        });
      }
      if (t.status === 'closed') {
        const exitT = isoToUtcSeconds(t.exitTime);
        if (exitT != null && exitT >= range.firstT && exitT <= range.lastT) {
          const pnlText =
            t.pnl >= 0 ? `+$${t.pnl.toFixed(0)}` : `-$${Math.abs(t.pnl).toFixed(0)}`;
          // PX5: trades con closeReason 'polling-mid-candle-*' salieron
          // intra-candle (modo polling 5s). Prefijamos el marker con ⚡ para
          // distinguirlos visualmente de los exits al cierre del minuto.
          const isPollingExit =
            typeof t.closeReason === 'string' && t.closeReason.startsWith('polling-');
          const prefix = isPollingExit ? '⚡ ' : '';
          markers.push({
            time: exitT as UTCTimestamp,
            position: t.pnl >= 0 ? 'aboveBar' : 'belowBar',
            color: t.pnl >= 0 ? theme.success : theme.destructive,
            shape: t.pnl >= 0 ? 'circle' : 'square',
            text: `${prefix}${pnlText} #${num}`,
          });
        }
      }
    }
    markers.sort((a, b) => (a.time as number) - (b.time as number));
    return markers;
  }

  // ----- Session boundary markers en el candleSeries -----
  function buildSessionBoundaryMarkers() {
    const out: Array<{
      time: UTCTimestamp;
      position: 'aboveBar';
      color: string;
      shape: 'circle';
      text: string;
    }> = [];
    if (sessionStartIso) {
      const t = isoToUtcSeconds(sessionStartIso);
      if (t != null) {
        out.push({
          time: t as UTCTimestamp,
          position: 'aboveBar',
          color: theme.gold,
          shape: 'circle',
          text: 'Start',
        });
      }
    }
    if (sessionEndIso) {
      const t = isoToUtcSeconds(sessionEndIso);
      if (t != null) {
        out.push({
          time: t as UTCTimestamp,
          position: 'aboveBar',
          color: theme.muted,
          shape: 'circle',
          text: 'End',
        });
      }
    }
    return out;
  }

  // ----- Crosshair tooltip enriquecido -----
  let tooltipVisible = $state(false);
  let tooltipContent = $state<{
    time: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
  } | null>(null);
  let tooltipX = $state(0);
  let tooltipY = $state(0);

  function setupCrosshair() {
    if (!chart || !candleSeries) return;
    chart.subscribeCrosshairMove((param) => {
      if (
        !param.point ||
        !candleSeries ||
        param.point.x < 0 ||
        param.point.y < 0 ||
        !param.time
      ) {
        tooltipVisible = false;
        return;
      }
      const candleData = param.seriesData.get(candleSeries) as
        | { open: number; high: number; low: number; close: number }
        | undefined;
      if (!candleData) {
        tooltipVisible = false;
        return;
      }
      const t = param.time as number;
      const date = new Date(t * 1000);
      const etFmt = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
      let volume = 0;
      if (volumeSeries) {
        const v = param.seriesData.get(volumeSeries) as { value: number } | undefined;
        volume = v?.value ?? 0;
      }
      tooltipContent = {
        time: etFmt.format(date),
        open: candleData.open,
        high: candleData.high,
        low: candleData.low,
        close: candleData.close,
        volume,
      };
      tooltipVisible = true;
      tooltipX = param.point.x + 12;
      tooltipY = param.point.y + 12;
    });
  }

  // ----- Mount + resize observer -----
  onMount(() => {
    theme = getChartTheme();
    const initialWidth = container.clientWidth > 0 ? container.clientWidth : 600;
    chart = createChart(container, {
      width: initialWidth,
      height,
      layout: {
        background: { color: theme.background },
        textColor: theme.foreground,
        fontFamily: 'Inter, system-ui, sans-serif',
      },
      grid: {
        vertLines: { color: theme.grid, style: 0, visible: true },
        horzLines: { color: theme.grid, style: 0, visible: true },
      },
      rightPriceScale: {
        borderColor: theme.border,
        scaleMargins: { top: 0.05, bottom: 0.25 },
      },
      timeScale: {
        borderColor: theme.border,
        timeVisible: true,
        secondsVisible: false,
        // El operador trabaja en ET. Lightweight muestra UTC por default;
        // forzamos el axis a wall-clock ET con tickMarkFormatter.
        tickMarkFormatter: (time: Time, tickMarkType: number) =>
          tickMarkFormatterET(time as number, tickMarkType),
      },
      crosshair: {
        mode: 1, // Magnet
      },
      // Controls: mouse wheel zoom + click+drag pan en el chart.
      // axisPressedMouseMove off — el operador se confundia arrastrando cerca
      // del eje (tiraba zoom en vez de pan).
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
      handleScale: {
        mouseWheel: true,
        pinch: true,
        axisPressedMouseMove: false,
        axisDoubleClickReset: true,
      },
    });

    candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: theme.success,
      downColor: theme.destructive,
      borderVisible: false,
      wickUpColor: theme.success,
      wickDownColor: theme.destructive,
    });

    volumeSeries = chart.addSeries(
      HistogramSeries,
      {
        color: theme.muted,
        priceFormat: { type: 'volume' },
        priceScaleId: 'volume',
      },
      // Pane index 1 = abajo del price pane
      1,
    );
    chart.panes()[1]?.setHeight(height * 0.15);

    markersPlugin = createSeriesMarkers(candleSeries, []);

    refreshData();
    setupCrosshair();

    // ResizeObserver ajusta width Y height del chart al tamano real del
    // container. Esto cubre tanto resize de ventana como entrada/salida de
    // fullscreen (cuando el wrapper crece a 100vh, el container hereda altura
    // via CSS y disparamos el resize del chart Canvas).
    const ro = new ResizeObserver(() => {
      if (!chart || !container) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w > 0 && h > 0) chart.applyOptions({ width: w, height: h });
    });
    ro.observe(container);

    if (onReady) onReady(chart);

    return () => {
      ro.disconnect();
      chart?.remove();
      chart = null;
    };
  });

  // ----- Reactivity: re-build data when inputs change -----
  function refreshData() {
    if (!chart || !candleSeries || !volumeSeries) return;
    const ohlc = getOhlc();
    // biome-ignore lint/suspicious/noExplicitAny: Lightweight types con HorzScaleItem genericos
    candleSeries.setData(ohlc.map((c) => ({ ...c, time: c.time as UTCTimestamp })) as any);
    // biome-ignore lint/suspicious/noExplicitAny: idem
    volumeSeries.setData(getVolumeData() as any);
    if (markersPlugin) {
      const all = [...buildSessionBoundaryMarkers(), ...buildMarkers()];
      all.sort((a, b) => (a.time as number) - (b.time as number));
      // biome-ignore lint/suspicious/noExplicitAny: SeriesMarker generic
      markersPlugin.setMarkers(all as any);
    }
    chart.timeScale().fitContent();
  }

  $effect(() => {
    // Re-render cuando cambian inputs (deep tracking via referencia)
    void candles;
    void trades;
    void heikinAshi;
    void sessionStartIso;
    void sessionEndIso;
    untrack(() => refreshData());
  });

  // ----- Theme reactivity -----
  $effect(() => {
    void themeStore.theme;
    untrack(() => {
      if (!chart) return;
      theme = getChartTheme();
      chart.applyOptions({
        layout: {
          background: { color: theme.background },
          textColor: theme.foreground,
        },
        grid: {
          vertLines: { color: theme.grid },
          horzLines: { color: theme.grid },
        },
        rightPriceScale: { borderColor: theme.border },
        timeScale: { borderColor: theme.border },
      });
      candleSeries?.applyOptions({
        upColor: theme.success,
        downColor: theme.destructive,
        wickUpColor: theme.success,
        wickDownColor: theme.destructive,
      });
      refreshData();
    });
  });
</script>

<div
  class="chart-host relative w-full"
  style="--chart-h: {height}px; height: var(--chart-h);"
>
  <div bind:this={container} class="absolute inset-0"></div>

  <!-- Crosshair tooltip -->
  {#if tooltipVisible && tooltipContent}
    <div
      class="pointer-events-none absolute z-10 rounded-lg border border-border bg-surface-3/95 px-3 py-2 shadow-luxury backdrop-blur lifted-edge"
      style="left: {tooltipX}px; top: {tooltipY}px;"
    >
      <div class="text-muted-foreground text-[10px] uppercase tracking-wider">
        {tooltipContent.time} ET
      </div>
      <div class="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono text-xs tabular-nums">
        <span class="text-muted-foreground">O</span>
        <span>{tooltipContent.open.toFixed(2)}</span>
        <span class="text-muted-foreground">H</span>
        <span class="text-success">{tooltipContent.high.toFixed(2)}</span>
        <span class="text-muted-foreground">L</span>
        <span class="text-destructive">{tooltipContent.low.toFixed(2)}</span>
        <span class="text-muted-foreground">C</span>
        <span
          class={tooltipContent.close >= tooltipContent.open
            ? 'text-success'
            : 'text-destructive'}
        >
          {tooltipContent.close.toFixed(2)}
        </span>
        <span class="text-muted-foreground">V</span>
        <span>{tooltipContent.volume.toLocaleString()}</span>
      </div>
    </div>
  {/if}
</div>
